/**
 * v5.10 A3 — Gate Run confirmation screen.
 * Three columns: Criterion | Suggested Score | Reasoning + Citations.
 * Polls run status while queued/running; on completion becomes read-only.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useTerm } from "@/lib/terminology.jsx";
import {
  ArrowLeft, Loader2, CheckCircle2, XCircle, RefreshCw,
  ExternalLink, AlertTriangle, ShieldCheck,
} from "lucide-react";

const POLL_MS = 2500;

export default function GateRunReview() {
  const { runId } = useParams();
  const nav = useNavigate();
  const term = useTerm();
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({}); // score_id -> {value, reasoning}
  const [saving, setSaving] = useState({});
  const [confirmAllBusy, setConfirmAllBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/gate-runs/${runId}`);
    setData(data);
    return data;
  }, [runId]);

  useEffect(() => {
    let alive = true;
    let tid;
    const tick = async () => {
      try {
        const d = await load();
        if (!alive) return;
        if (d.run.status === "queued" || d.run.status === "running") {
          tid = setTimeout(tick, POLL_MS);
        }
      } catch (e) {
        toast.error("Failed to load run");
      }
    };
    tick();
    return () => { alive = false; if (tid) clearTimeout(tid); };
  }, [load]);

  const setEdit = (score_id, patch) => setEdits((e) => ({
    ...e, [score_id]: { ...(e[score_id] || {}), ...patch },
  }));

  const confirmOne = async (s) => {
    const v = edits[s.score_id]?.value ?? s.suggested_value ?? "";
    const rea = edits[s.score_id]?.reasoning ?? s.suggested_reasoning ?? "";
    if (!v && v !== 0) { toast.error("Value required"); return; }
    setSaving((x) => ({ ...x, [s.score_id]: true }));
    try {
      const { data: res } = await api.post(
        `/gate-runs/${runId}/scores/${s.score_id}/confirm`,
        { final_value: String(v), final_reasoning: rea },
      );
      toast.success("Confirmed");
      if (res.auto_move) {
        toast.success(`Auto-moved to ${res.auto_move.target_stage_name}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to confirm");
    } finally {
      setSaving((x) => ({ ...x, [s.score_id]: false }));
    }
  };

  const confirmAllRemaining = async () => {
    setConfirmAllBusy(true);
    try {
      const { data: res } = await api.post(`/gate-runs/${runId}/confirm-all`);
      toast.success("Confirmed all with AI suggestions");
      if (res.auto_move) {
        toast.success(`Auto-moved to ${res.auto_move.target_stage_name}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setConfirmAllBusy(false);
    }
  };

  const retry = async () => {
    try {
      await api.post(`/gate-runs/${runId}/retry`);
      toast.success("Retrying");
      setEdits({});
      await load();
      // Continue polling
      setTimeout(async function loop() {
        const d = await load();
        if (d.run.status === "queued" || d.run.status === "running") {
          setTimeout(loop, POLL_MS);
        }
      }, POLL_MS);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const summary = useMemo(() => {
    if (!data) return null;
    const scores = data.scores || [];
    const isCompleted = data.run.status === "completed";
    let total = 0, maxTotal = 0, passCount = 0, failCount = 0;
    for (const s of scores) {
      const val = isCompleted ? s.final_value : s.suggested_value;
      if (s.criteria_type === "scored") {
        const n = parseInt(val || 0, 10) || 0;
        total += n;
        maxTotal += s.max_score_at_run || 0;
      } else if (s.criteria_type === "pass_fail") {
        if (String(val).toLowerCase() === "pass") passCount++;
        else if (String(val).toLowerCase() === "fail") failCount++;
      }
    }
    return { total, maxTotal, passCount, failCount };
  }, [data]);

  if (!data) return <div className="p-10 text-[var(--cw-muted)]">Loading run…</div>;

  const isRunning = data.run.status === "queued" || data.run.status === "running";
  const isCompleted = data.run.status === "completed";
  const isFailed = data.run.status === "failed";
  const isManual = data.run.run_mode === "manual";
  const scores = data.scores || [];
  const confirmedCount = scores.filter((s) => s.is_confirmed).length;

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <button onClick={() => nav(`/deals/${data.run.deal_id}`)}
        className="text-sm inline-flex items-center gap-1 text-[var(--cw-muted)] hover:text-[var(--cw-primary)]"
        data-testid="back-to-deal">
        <ArrowLeft size={14} /> Back to {term("deal").toLowerCase()}
      </button>

      <div className="mt-4 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="cw-caption uppercase tracking-widest">
            {isManual
              ? (isCompleted ? "Gate Result · Manual" : "Manual Gate Run")
              : (isCompleted ? "Gate Result · AutoGate" : "AutoGate Confirmation")}
          </div>
          <h1 className="cw-h1 mt-1 inline-flex items-center gap-2">
            <ShieldCheck size={22} /> {data.run.gate_name}
          </h1>
          <div className="text-sm text-[var(--cw-muted)] mt-1">
            {data.deal.company_name}
            {data.run.run_source === "auto_stage_entry" && " · Auto-run on entry"}
            {data.run.triggered_by_name && ` · Triggered by ${data.run.triggered_by_name}`}
          </div>
          {summary && (
            <div className="mt-2 text-sm font-medium">
              {data.run.gate_type === "hard_filter"
                ? `${summary.passCount} of ${scores.length} Pass ${isCompleted ? "confirmed" : "suggested"}`
                : `${summary.total}/${summary.maxTotal || "—"} ${isCompleted ? "confirmed" : "suggested"}`}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isRunning && (
            <span className="cw-badge inline-flex items-center gap-1"
              style={{ background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" }}>
              <Loader2 size={12} className="animate-spin" /> Scoring…
            </span>
          )}
          {isCompleted && (
            <span className="cw-badge" style={{ background: "rgba(44,154,82,0.12)", color: "#1F6E3A" }}>
              <CheckCircle2 size={12} /> Completed
            </span>
          )}
          {isFailed && (
            <button onClick={retry} className="cw-btn-primary text-sm inline-flex items-center gap-1">
              <RefreshCw size={12} /> Retry
            </button>
          )}
          {!isCompleted && !isRunning && !isFailed && confirmedCount < scores.length && !isManual && (
            <button onClick={confirmAllRemaining} disabled={confirmAllBusy}
              className="cw-btn-secondary text-sm inline-flex items-center gap-1"
              data-testid="confirm-all-btn">
              <CheckCircle2 size={12} />
              {confirmAllBusy ? "Confirming…" : "Confirm all remaining"}
            </button>
          )}
        </div>
      </div>

      {isFailed && (
        <div className="mt-4 rounded-md p-3"
          style={{ background: "rgba(214,69,69,0.10)", color: "var(--cw-error)" }}>
          <AlertTriangle size={14} className="inline mr-1" />
          {data.run.error || "Scoring failed"}
        </div>
      )}

      {isRunning && (
        <div className="mt-8 cw-card p-10 text-center">
          <Loader2 className="animate-spin mx-auto text-[var(--cw-primary)]" size={30} />
          <div className="cw-h3 mt-3">Reading the deal…</div>
          <div className="text-sm text-[var(--cw-muted)] mt-1">
            Claude is scoring each criterion against stored documents + fields.
          </div>
        </div>
      )}

      {!isRunning && scores.length > 0 && (
        <>
          <div className="text-xs text-[var(--cw-muted)] mt-4">
            {confirmedCount} of {scores.length} criteria confirmed
          </div>
          <div className="mt-3 space-y-3">
            {scores.map((s) => (
              <ScoreRow
                key={s.score_id}
                score={s}
                isCompleted={isCompleted}
                isManual={isManual}
                edit={edits[s.score_id] || {}}
                setEdit={(patch) => setEdit(s.score_id, patch)}
                onConfirm={() => confirmOne(s)}
                saving={saving[s.score_id]}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ScoreRow({ score, isCompleted, edit, setEdit, onConfirm, saving, isManual }) {
  const confirmed = score.is_confirmed;
  const isHardFilter = score.criteria_type === "pass_fail";
  const value = confirmed
    ? score.final_value
    : (edit.value != null ? edit.value : score.suggested_value ?? "");
  const reasoning = confirmed
    ? (score.final_reasoning || "")
    : (edit.reasoning != null ? edit.reasoning : (score.suggested_reasoning || ""));

  const border = { borderColor: "var(--cw-border)" };
  return (
    <div className={`cw-card p-4 ${confirmed ? "opacity-80" : ""}`}
      data-testid={`score-row-${score.score_id}`}>
      <div className="grid gap-4 lg:grid-cols-[1fr,240px,1.5fr]">
        {/* Col 1: Criterion */}
        <div>
          <div className="text-sm font-semibold">{score.criteria_text}</div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--cw-muted)] mt-1">
            {isHardFilter ? "Pass / Fail" : `Score / ${score.max_score_at_run || "—"}`}
          </div>
          {confirmed && (
            <div className="text-[10px] mt-2 text-[var(--cw-muted)]">
              Confirmed by {score.confirmed_by_name || "user"} · {score.confirmed_at}
            </div>
          )}
        </div>

        {/* Col 2: Score (type-dependent) */}
        <div>
          {isHardFilter ? (
            <div className="flex gap-1">
              {["Pass", "Fail"].map((opt) => {
                const sel = String(value).toLowerCase() === opt.toLowerCase();
                const color = opt === "Pass" ? "#1F6E3A" : "var(--cw-error)";
                const bg = sel ? (opt === "Pass" ? "rgba(44,154,82,0.14)" : "rgba(214,69,69,0.12)") : "transparent";
                return (
                  <button key={opt} disabled={confirmed}
                    onClick={() => setEdit({ value: opt })}
                    className="flex-1 h-9 rounded-md border text-sm font-semibold transition"
                    style={{ ...border, borderColor: sel ? color : "var(--cw-border)",
                             background: bg, color: sel ? color : "var(--cw-muted)" }}
                    data-testid={`score-${score.score_id}-${opt.toLowerCase()}`}>
                    {opt === "Pass" ? <CheckCircle2 size={14} className="inline mr-1" /> : <XCircle size={14} className="inline mr-1" />}
                    {opt}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input type="number" min="0" max={score.max_score_at_run || undefined}
                value={value} disabled={confirmed}
                onChange={(e) => setEdit({ value: e.target.value })}
                className="w-full h-9 px-3 rounded-md border text-sm" style={border}
                data-testid={`score-${score.score_id}-input`} />
              <span className="text-xs text-[var(--cw-muted)]">/ {score.max_score_at_run || "—"}</span>
            </div>
          )}
        </div>

        {/* Col 3: Reasoning + Citations */}
        <div>
          <textarea value={reasoning} disabled={confirmed}
            placeholder={isManual ? "Explain why you picked this value" : "AI reasoning appears here — edit if needed"}
            onChange={(e) => setEdit({ reasoning: e.target.value })}
            className="w-full min-h-[80px] px-3 py-2 rounded-md border text-sm" style={border}
            data-testid={`reasoning-${score.score_id}`} />
          {!isManual && score.citations && score.citations.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {score.citations.map((c, i) => (
                <a key={i} href={c.source_url || "#"} target="_blank" rel="noreferrer"
                  className="text-[10px] inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full border"
                  style={{ borderColor: "var(--cw-border)", color: "var(--cw-primary)" }}
                  onClick={(e) => { if (!c.source_url) e.preventDefault(); }}>
                  {c.source_type} · {c.source_ref || "citation"} <ExternalLink size={9} />
                </a>
              ))}
            </div>
          )}
          {isManual && !confirmed && (
            <div className="mt-2 text-[10px] uppercase tracking-widest text-[var(--cw-muted)]">
              Manual entry — no AI
            </div>
          )}
          {!confirmed && (
            <div className="mt-2 text-right">
              <button onClick={onConfirm} disabled={saving}
                className="cw-btn-primary text-xs inline-flex items-center gap-1"
                data-testid={`confirm-${score.score_id}`}>
                <CheckCircle2 size={12} /> {saving ? "Confirming…" : "Confirm"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
